import React, { useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TopNavbar } from "@/components/layout";
import { TaskService } from "@/services/taskService";
import { useAllUsers } from "@/hooks/useUsers";
import { useTeams } from "@/hooks/useTeams";
import type { Task, LookupValue } from "@/types/task";
import {
  TbChevronLeft,
  TbChevronRight,
  TbCheck,
  TbClock,
  TbSearch,
  TbDownload,
  TbMaximize,
} from "react-icons/tb";

export const AssetDetails: React.FC = () => {
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<"open" | "closed" | "all">("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch user and team data for resolving assignee names
  const { data: users = [] } = useAllUsers();
  const { data: teams = [] } = useTeams();

  // Fetch all tasks for this asset
  const {
    data: assetTasks = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["asset-tasks", assetId],
    queryFn: () => TaskService.getTasksByAssetId(assetId!),
    enabled: !!assetId,
  });

  // Helper function to get assignee display name
  const getAssigneeDisplayName = useCallback(
    (task: Task) => {
      if (task.assignee_type === "User") {
        const user = users.find((u) => u.id === task.assignee);
        if (user) {
          return user.first_name || user.last_name
            ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
            : user.email;
        }
      } else if (task.assignee_type === "Team") {
        const team = teams.find((t) => t.id === task.assignee);
        if (team) {
          return team.name;
        }
      }
      return "Unassigned";
    },
    [users, teams]
  );

  // Get asset information from the first task (assuming all tasks for same asset have same asset details)
  const assetInfo = assetTasks[0] || null;

  // Filter tasks based on active tab
  const filteredTasks = assetTasks.filter((task: Task) => {
    const matchesSearch =
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      task.id.toLowerCase().includes(searchTerm.toLowerCase());

    if (!matchesSearch) return false;

    switch (activeTab) {
      case "open":
        return !task.completed_at;
      case "closed":
        return !!task.completed_at;
      case "all":
      default:
        return true;
    }
  });

  // Group tasks by year for timeline
  const tasksByYear = assetTasks.reduce(
    (acc: Record<number, Task[]>, task: Task) => {
      const year = new Date(task.created_at).getFullYear();
      if (!acc[year]) acc[year] = [];
      acc[year].push(task);
      return acc;
    },
    {} as Record<number, Task[]>
  );

  const years = Object.keys(tasksByYear)
    .map(Number)
    .sort((a, b) => a - b);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopNavbar />
        <div className="flex justify-center items-center py-20">
          <div className="text-gray-500">Loading asset details...</div>
        </div>
      </div>
    );
  }

  if (error || !assetInfo) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TopNavbar />
        <div className="flex justify-center items-center py-20">
          <div className="text-red-500">
            {error ? "Error loading asset details" : "Asset not found"}
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getStatusColor = (status?: LookupValue) => {
    switch (status?.value) {
      case "completed":
        return "text-green-800 bg-green-100";
      case "in_progress":
        return "text-blue-800 bg-blue-100";
      case "pending":
        return "text-yellow-800 bg-yellow-100";
      default:
        return "text-gray-800 bg-gray-100";
    }
  };

  const getPriorityColor = (priority?: LookupValue) => {
    switch (priority?.value) {
      case "urgent":
        return "text-red-800 bg-red-100";
      case "high":
        return "text-orange-800 bg-orange-100";
      case "medium":
        return "text-yellow-800 bg-yellow-100";
      case "low":
        return "text-green-800 bg-green-100";
      default:
        return "text-gray-800 bg-gray-100";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNavbar />

      <div className="max-w-7xl mx-auto p-6">
        {/* Header with breadcrumbs */}
        <div className="flex items-center gap-2 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
          >
            <TbChevronLeft size={20} className="text-gray-600" />
          </button>
          <nav className="flex items-center gap-1 text-sm text-gray-600">
            <span>Home</span>
            <TbChevronRight size={16} />
            <span>Assets</span>
            <TbChevronRight size={16} />
            <span>Asset Name</span>
          </nav>
          <div className="ml-auto flex gap-2">
            <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <TbSearch size={20} className="text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <TbDownload size={20} className="text-gray-600" />
            </button>
            <button className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <TbMaximize size={20} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Asset Lifecycle Header */}
        <h1 className="text-3xl font-bold text-gray-900 mb-8">
          ASSET LIFECYCLE
        </h1>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
          {/* Asset Summary */}
          <div className="xl:col-span-2 bg-white rounded-lg border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              ASSET SUMMARY
            </h2>
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset ID
                  </label>
                  <div className="p-2 border rounded">{assetInfo.asset_id}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <div className="p-2 border rounded">
                    {assetInfo.geom ? "Coordinates Available" : "No Location"}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Install Date
                  </label>
                  <div className="p-2 border rounded">
                    {formatDate(assetInfo.created_at)}
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Asset Type
                  </label>
                  <div className="p-2 border rounded">
                    {assetInfo.layer_name || "N/A"}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Condition
                  </label>
                  <div className="p-2 border rounded">
                    {assetInfo.status?.label || "Unknown"}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Owner
                  </label>
                  <div className="p-2 border rounded">System</div>
                </div>
              </div>
            </div>
          </div>

          {/* Chart placeholder */}
          <div className="bg-white rounded-lg border p-6">
            <div className="h-48 bg-gray-100 rounded flex items-center justify-center">
              <span className="text-gray-500">Task Timeline Chart</span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-lg border p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">TIMELINE</h2>
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              {years.map((year, index) => (
                <div key={year} className="flex-1 text-center relative">
                  <span className="text-sm font-medium text-gray-700">
                    {year}
                  </span>
                  <div className="absolute top-6 left-1/2 transform -translate-x-1/2">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                  </div>
                  {index < years.length - 1 && (
                    <div className="absolute top-7 left-1/2 w-full h-0.5 bg-gray-300"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Task Tabs and Table */}
        <div className="bg-white rounded-lg border">
          {/* Tab Navigation */}
          <div className="border-b">
            <nav className="flex">
              {[
                { key: "open", label: "OPEN TASKS" },
                { key: "closed", label: "CLOSED TASKS" },
                { key: "all", label: "ALL TASKS" },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() =>
                    setActiveTab(tab.key as "open" | "closed" | "all")
                  }
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Search */}
          <div className="p-4 border-b">
            <div className="relative">
              <TbSearch
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Task Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Assigned To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Outcome
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTasks.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      No tasks found
                    </td>
                  </tr>
                ) : (
                  filteredTasks.map((task: Task) => (
                    <tr key={task.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(task.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">
                        {task.id.slice(0, 8)}...
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {task.category?.label || "General"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getAssigneeDisplayName(task)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            task.status
                          )}`}
                        >
                          {task.status?.label || "Unknown"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(
                            task.priority
                          )}`}
                        >
                          {task.priority?.label || "Medium"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {task.completed_at ? (
                          <TbCheck className="text-green-600" size={16} />
                        ) : (
                          <TbClock className="text-yellow-600" size={16} />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};
